/**
 * Provider-Aware Authentication Types
 * 
 * Discriminated union types that distinguish between MSAL and REST/OAuth2 providers.
 * This makes the type system aware of which provider is being used, preventing
 * accidental calls to refreshTokenApi() when using MSAL.
 */

import type { PublicClientApplication, AccountInfo } from '@azure/msal-browser';

/**
 * Provider type discriminator
 */
export type AuthProviderType = 'msal' | 'rest' | 'oauth2';

/**
 * Base interface for provider-aware context
 */
interface BaseAuthProviderContext {
  providerType: AuthProviderType;
}

/**
 * MSAL Provider Context
 * MSAL uses acquireTokenSilent() and does NOT use refreshTokenApi()
 */
export interface MsalProviderContext extends BaseAuthProviderContext {
  providerType: 'msal';
  msalInstance: PublicClientApplication;
  account: AccountInfo | null;
  // MSAL does NOT have refreshToken - it uses acquireTokenSilent()
  // refreshTokenApi() should NEVER be called for MSAL
}

/**
 * REST Provider Context
 * REST uses refreshTokenApi() for token refresh
 */
export interface RestProviderContext extends BaseAuthProviderContext {
  providerType: 'rest';
  // REST providers use refreshTokenApi() for token refresh
  refreshToken: string | null;
}

/**
 * OAuth2 Provider Context
 * OAuth2 uses refreshTokenApi() for token refresh (same as REST)
 */
export interface OAuth2ProviderContext extends BaseAuthProviderContext {
  providerType: 'oauth2';
  // OAuth2 providers use refreshTokenApi() for token refresh
  refreshToken: string | null;
}

/**
 * Discriminated union of all provider contexts
 */
export type AuthProviderContext = 
  | MsalProviderContext 
  | RestProviderContext 
  | OAuth2ProviderContext;

/**
 * Type guard: Check if context is MSAL provider
 */
export function isMsalProvider(context: AuthProviderContext): context is MsalProviderContext {
  return context.providerType === 'msal';
}

/**
 * Type guard: Check if context is REST provider
 */
export function isRestProvider(context: AuthProviderContext): context is RestProviderContext {
  return context.providerType === 'rest';
}

/**
 * Type guard: Check if context is OAuth2 provider
 */
export function isOAuth2Provider(context: AuthProviderContext): context is OAuth2ProviderContext {
  return context.providerType === 'oauth2';
}

/**
 * Type guard: Check if provider uses refreshTokenApi()
 * Only REST and OAuth2 use refreshTokenApi(), MSAL does not
 */
export function usesRefreshTokenApi(context: AuthProviderContext): context is RestProviderContext | OAuth2ProviderContext {
  return context.providerType === 'rest' || context.providerType === 'oauth2';
}

/**
 * Helper: Get refresh token from context (only for REST/OAuth2)
 * Type-safe: Returns null for MSAL (which doesn't have refresh tokens)
 */
export function getRefreshTokenFromContext(context: AuthProviderContext): string | null {
  if (usesRefreshTokenApi(context)) {
    return context.refreshToken;
  }
  // MSAL doesn't have refresh tokens
  return null;
}

/**
 * Helper: Get MSAL instance from context (only for MSAL)
 * Type-safe: Returns null for non-MSAL providers
 */
export function getMsalInstanceFromContext(context: AuthProviderContext): PublicClientApplication | null {
  if (isMsalProvider(context)) {
    return context.msalInstance;
  }
  return null;
}

/**
 * Helper: Get account from context (only for MSAL)
 * Type-safe: Returns null for non-MSAL providers
 */
export function getAccountFromContext(context: AuthProviderContext): AccountInfo | null {
  if (isMsalProvider(context)) {
    return context.account;
  }
  return null;
}

/**
 * Create provider context from current auth state
 * This helper creates a provider-aware context based on AUTH_API_CONFIG
 * 
 * @param refreshToken - Current refresh token (for REST/OAuth2 providers)
 * @param msalInstance - MSAL instance (for MSAL provider)
 * @param account - MSAL account (for MSAL provider)
 * @returns Provider-aware context matching current configuration
 */
export function createProviderContext(
  refreshToken: string | null,
  msalInstance?: PublicClientApplication | null,
  account?: AccountInfo | null
): AuthProviderContext {
  const providerType = (import.meta.env.VITE_AUTH_PROVIDER_TYPE as AuthProviderType) ?? 'rest';
  
  if (providerType === 'msal') {
    if (!msalInstance) {
      throw new Error('MSAL provider requires msalInstance');
    }
    return {
      providerType: 'msal',
      msalInstance,
      account: account ?? null,
    };
  } else if (providerType === 'oauth2') {
    return {
      providerType: 'oauth2',
      refreshToken,
    };
  } else {
    // Default to REST
    return {
      providerType: 'rest',
      refreshToken,
    };
  }
}
